/**
 * Maps assistant-service `/chat/history/archive` rows into the `sessions` object
 * used by chat session pickers (`main.tsx`, immigration applicant chat).
 */

export type ChatSessionQuestion = {
  question?: string;
  epochTime?: number;
  isDeepDive?: boolean;
  isMobile?: boolean;
};

export type ChatSessionRow = {
  sessionId: string;
  questions: ChatSessionQuestion[];
};

export type RefreshChatHistorySessionsOptions = {
  /** When true, replace the list with a loading spinner until the fetch returns. */
  showLoading?: boolean;
  /** If this session id is missing from the archive, retry twice with backoff. */
  expectedSessionId?: string | null;
  /**
   * Follow-up queries reuse an existing session. Retry while that session's
   * latest archived question is older than this epoch (TR-2957).
   */
  expectedQuestionEpoch?: number | null;
};

/** False when the archive is still missing the expected session or its latest turn. */
export function archiveHasExpectedTurn(
  sessions: ChatSessionRow[],
  options?: {
    expectedSessionId?: string | null;
    expectedQuestionEpoch?: number | null;
  },
): boolean {
  const expectedId = options?.expectedSessionId?.trim();
  if (!expectedId) return true;
  const session = sessions.find((row) => row.sessionId === expectedId);
  if (!session) return false;
  const expectedEpoch = options?.expectedQuestionEpoch;
  if (expectedEpoch == null || !Number.isFinite(expectedEpoch)) return true;
  return getSessionLatestQuestionEpoch(session) + 1e-6 >= expectedEpoch;
}

/** Latest submitted query epoch for a session (seconds; may include ms fraction). */
export function getSessionLatestQuestionEpoch(session: ChatSessionRow): number {
  if (!session.questions?.length) return 0;
  return Math.max(
    ...session.questions.map((question) => Number(question.epochTime ?? 0)),
  );
}

/** Most recently submitted question in a session (by epochTime). */
export function getSessionLatestQuestion(
  session: ChatSessionRow,
): ChatSessionQuestion | undefined {
  if (!session.questions?.length) return undefined;
  return [...session.questions].sort(
    (a, b) => Number(a.epochTime ?? 0) - Number(b.epochTime ?? 0),
  ).at(-1);
}

/**
 * Sort sessions by the most recent submitted query (newest first). Questions
 * within each session are ordered chronologically for tooltips / prompt lists.
 */
export function sortChatSessionsByLatestQuery<T extends ChatSessionRow>(
  sessions: T[],
): T[] {
  return [...sessions]
    .filter((session) => session.questions?.length > 0)
    .map((session) => ({
      ...session,
      questions: [...session.questions].sort(
        (a, b) => Number(a.epochTime ?? 0) - Number(b.epochTime ?? 0),
      ),
    }))
    .sort(
      (a, b) =>
        getSessionLatestQuestionEpoch(b) - getSessionLatestQuestionEpoch(a),
    );
}

/**
 * True when history may still live under ``{display name}-{userId}`` as well as
 * ``{folder token}-{userId}``.
 *
 * Writes before the TR-359 assistant/frontend pair keyed ``chat_key`` on the
 * display name. After deploy, reads prefer the folder token. A token-bearing
 * case therefore has two partitions until those older rows age out — both must
 * be queried and kept, or prior threads vanish from the picker.
 *
 * Legacy (token-less) cases and All Cases have one key; skip the extra read.
 */
export function shouldReadLegacyDisplayNamePartition(
  caseId: string | null | undefined,
  caseFolderName: string | null | undefined,
): boolean {
  const name = (caseId ?? '').trim();
  const folderName = (caseFolderName ?? '').trim();
  if (!name || !folderName) return false;
  if (name === '*' || name.toLowerCase() === 'all cases') return false;
  return folderName !== name;
}

function acceptedChatKeys(
  caseFolderName: string,
  userId: string,
  displayName?: string | null,
): Set<string> {
  const user = userId.trim();
  const keys = new Set<string>([`${caseFolderName}-${user}`]);
  const name = (displayName ?? '').trim();
  if (name && name !== caseFolderName) {
    keys.add(`${name}-${user}`);
  }
  return keys;
}

/**
 * Keep only this (case, user)'s rows, matching on ``chat_key``.
 *
 * ``chat_key`` is the archive's partition key: ``{folder name}-{userId}``,
 * where the folder name is fixed when the case is created. ``case_id`` is the case's
 * *display name* at the time the turn was written, which a rename changes — so
 * preferring ``case_id`` (as this did) dropped every pre-rename session out of the
 * picker even though the rows were sitting in the right partition (TR-359).
 *
 * When ``displayName`` differs from the folder name, also accept the pre-TR-359 key
 * ``{display name}-{userId}`` so auto/gated turns written before the folder-token
 * partition existed still appear.
 *
 * ``case_id`` / ``user_id`` remain the fallback for rows that predate ``chat_key``
 * or somehow lack it; for those a rename is unrecoverable either way, and for a
 * legacy case the folder name and the display name are the same string.
 */
function itemMatchesCaseUser(
  item: Record<string, unknown>,
  caseFolderName: string,
  userId: string,
  displayName?: string | null,
): boolean {
  const accepted = acceptedChatKeys(caseFolderName, userId, displayName);
  if (typeof item.chat_key === 'string' && item.chat_key !== '') {
    return accepted.has(item.chat_key);
  }
  const name = (displayName ?? '').trim();
  return (
    (item.case_id === caseFolderName || (name !== '' && item.case_id === name)) &&
    item.user_id === userId
  );
}

function isTimelineHistoryItem(item: Record<string, unknown>): boolean {
  return item.request_type === 'timeline' || item.request_type === 'timeline_build';
}

/**
 * Map assistant-service archive rows into the `sessions` payload clients expect.
 *
 * ``caseFolderName`` is the rename-stable case identifier, not the display name —
 * see {@link itemMatchesCaseUser}. Callers get it from
 * ``resolveCaseFolderName``; for a legacy case it *is* the display name.
 *
 * ``displayName`` is the current case title (``selectedCase`` / ``caseId``).
 * Pass it so pre-token ``chat_key`` rows are not dropped after a UUID-folder
 * case starts sending ``caseFolderName``.
 */
export function buildSessionsFromHistoryItems(
  items: unknown[],
  caseFolderName: string,
  userId: string,
  displayName?: string | null,
): Record<string, Record<string, unknown>> {
  const sessions: Record<string, Record<string, unknown>> = {};
  for (const row of items) {
    if (!row || typeof row !== 'object') continue;
    const item = row as Record<string, unknown>;
    if (!itemMatchesCaseUser(item, caseFolderName, userId, displayName)) continue;
    if (!item.session_id || item.enrich_event === true || isTimelineHistoryItem(item)) continue;
    const sessionId = String(item.session_id);
    const question = item.question;
    const rawEpoch = item.epoch_time_secs;
    const epochTime =
      typeof rawEpoch === 'number' && Number.isFinite(rawEpoch)
        ? rawEpoch
        : typeof rawEpoch === 'string'
          ? Number.parseInt(rawEpoch, 10)
          : Number(rawEpoch);
    const isDeepDive = 'request_type' in item
      ? item.request_type === 'deep_dive'
      : (() => {
          const rawAnswer = item.answer ?? '{}';
          let ansObj: Record<string, unknown> = {};
          try {
            const parsed = typeof rawAnswer === 'string' ? JSON.parse(rawAnswer) : rawAnswer;
            ansObj = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
          } catch {
            /* ignore */
          }
          return 'messages' in ansObj && Array.isArray(ansObj.messages) && (ansObj.messages as unknown[]).length > 0;
        })();
    const isMobile = 'request_type' in item && item.request_type === 'mobile';

    if (!sessions[sessionId]) {
      sessions[sessionId] = {
        sessionId,
        questions: [{ question, epochTime, isDeepDive, isMobile }],
      };
    } else {
      (sessions[sessionId].questions as object[]).push({ question, epochTime, isDeepDive, isMobile });
    }
  }
  return sessions;
}
